import { FC, useCallback, useState } from 'react';
import { sortObjectArray, SortOrder } from 'utils/array';
import { Rollout, RolloutProgress } from 'models/rollout';
import Progressbar from 'components/UI/Progressbar';
import Table from 'components/UI/Table';
import TableHeader from 'components/UI/Table/TableHeader';
import TableRow from 'components/UI/Table/TableRow';
import TableHeaderCell from 'components/UI/Table/TableHeaderCell';
import { FormattedMessage } from 'react-intl';
import TableBody from 'components/UI/Table/TableBody';
import TableBodyCell from 'components/UI/Table/TableBodyCell';
import TableDataCell from 'components/UI/Table/TableDataCell';
import { useAccess } from 'hooks/useAccess';
import RolloutStartConfirmation from '../RolloutStartConfirmation';
import ArrowIcon from 'components/UI/Icon/ArrowIcon';
import * as routes from 'router/Routes';
import { useNavigate } from 'react-router';
import { useAppDispatch } from 'hooks/redux';
import ActionsButtons from '../ActionButtons';
import RolloutCancelConfirmation from '../RolloutCancelConfirmation';
import { getRollout } from 'store/rolloutState/rolloutState';

const headerCells = [
  {
    title: 'rollout.name',
    sortable: true,
  },
  {
    title: 'rollout.software',
    sortable: true,
  },
  {
    title: 'rollout.progress',
    sortable: false,
  },
  {
    title: 'rollout.status',
    sortable: true,
  },
];

export type TableRollout = {
  name: string;
  software: string;
  progress: RolloutProgress;
  status: string;
  link: string;
};

type RolloutTableProps = {
  rollouts: Rollout[];
  initialActiveColumn: number;
  initialSortOrder: SortOrder;
};

const RolloutTable: FC<RolloutTableProps> = ({
  rollouts,
  initialActiveColumn,
  initialSortOrder,
}) => {
  // State
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [sortOrder] = useState<SortOrder>(initialSortOrder);
  const [activeColumnIndex] = useState<number>(initialActiveColumn);
  const { startRolloutAccess } = useAccess();
  const [openConfirmStartModal, setOpenConfirmStartModal] =
    useState<boolean>(false);
  const [openConfirmCancelModal, setOpenConfirmCancelModal] =
    useState<boolean>(false);
  const [rollout, setRollout] = useState<TableRollout>();

  const onOpenStart = useCallback(
    (rollout: TableRollout) => {
      setOpenConfirmStartModal(true);
      setRollout(rollout);
    },
    [setOpenConfirmStartModal, setRollout]
  );

  const onOpenCancel = useCallback(
    (rollout: TableRollout) => {
      setOpenConfirmCancelModal(true);
      setRollout(rollout);
    },
    [setOpenConfirmCancelModal, setRollout]
  );

  const onCloseStart = useCallback(() => {
    setOpenConfirmStartModal(false);
    setRollout(undefined);
  }, [setOpenConfirmStartModal, setRollout]);

  const onCloseCancel = useCallback(() => {
    setOpenConfirmCancelModal(false);
    setRollout(undefined);
  }, [setOpenConfirmCancelModal, setRollout]);

  // Fetch rollout and navigate
  const onClick = useCallback(
    (id: string) => {
      if (id) {
        dispatch(getRollout(id));
      }
      navigate(`${routes.ROLLOUTS}/${id}`);
    },
    [dispatch, navigate]
  );

  const table: TableRollout[] = rollouts.map((rollout: Rollout) => {
    const { name, software, progress, status } = rollout.attributes;
    const rolloutTableItem = {
      name: name || '–',
      software: software || '–',
      progress: progress,
      status: status || '–',
      link: rollout.id || '–',
    };
    return rolloutTableItem;
  });

  // Sorted
  const sortedRollouts: TableRollout[] = sortObjectArray(
    table,
    Object.keys(table[0])[activeColumnIndex],
    sortOrder
  ) as TableRollout[];

  // Render arrow button
  const renderDetailsButton = useCallback(
    (link: string) => {
      if (!link) {
        return null;
      }
      return (
        <TableDataCell onClick={() => onClick(link)} isPressable>
          <ArrowIcon
            size="small"
            direction="right"
            transition={false}
            themeType="grey6"
          />
        </TableDataCell>
      );
    },
    [onClick]
  );

  const renderActionButtons = useCallback(
    (rollout: TableRollout) => {
      if (!startRolloutAccess || !onOpenStart || !rollout) {
        return null;
      }
      const enablePlayButton =
        rollout.status === 'ready' || rollout.status === 'paused';
      const enableCancelButton = rollout.status === 'running';
      return (
        <TableDataCell>
          <ActionsButtons
            onClickStart={() => onOpenStart(rollout)}
            onClickCancel={() => onOpenCancel(rollout)}
            enablePlayButton={enablePlayButton}
            enableCancelButton={enableCancelButton}
          />
        </TableDataCell>
      );
    },
    [onOpenStart, onOpenCancel, startRolloutAccess]
  );

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            {headerCells.map((cell, index) => {
              return (
                <TableHeaderCell key={index}>
                  {cell.title && <FormattedMessage id={cell.title} />}
                </TableHeaderCell>
              );
            })}
            {startRolloutAccess && ( // With right access level you allowed to do actions on a rollout
              <TableHeaderCell key={5}>
                <FormattedMessage id="rollout.actions" />
              </TableHeaderCell>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRollouts.map((item, rowIndex) => {
            return (
              <TableRow key={rowIndex}>
                {Object.entries(item).map(([name, value], colIndex) => {
                  if (name === 'progress') {
                    return (
                      <TableDataCell
                        key={`progressbar-{rowIndex}`}
                        displayType={'flow'}
                      >
                        <Progressbar
                          succeeded={(value as RolloutProgress).succeeded}
                          failed={(value as RolloutProgress).failed}
                          waiting={(value as RolloutProgress).waiting}
                          notStarted={(value as RolloutProgress).notStarted}
                          cancelled={(value as RolloutProgress).cancelled}
                          scheduled={(value as RolloutProgress).scheduled}
                        />
                      </TableDataCell>
                    );
                  }
                  return (
                    <TableBodyCell key={colIndex} name={name} value={value} />
                  );
                })}
                {renderActionButtons(item)}
                {renderDetailsButton(item.link)}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <RolloutStartConfirmation
        open={openConfirmStartModal}
        onClose={onCloseStart}
        rollout={rollout}
      />
      <RolloutCancelConfirmation
        open={openConfirmCancelModal}
        onClose={onCloseCancel}
        rollout={rollout}
      />
    </div>
  );
};

export default RolloutTable;
